import _ from 'lodash';

import PointCardDTO, { Contract } from '@/views/LatestEvent/models/PointCardDTO';
import { EventType } from '@/views/models/EventType';
import CourseCardPropDTO from '@/views/LatestEvent/models/CourseCardPropDTO';
import { getDateWithoutTime } from '@/plugins/utils';
import { CardResp, MerchantCardsResp, ContractCategoryResp } from '@/services/liff/models';
import { AccountResp } from '@/services/account.service';

interface MerchantState {
  profile: AccountResp
  tokens: CardResp[] // 商家啟用中的點卡
  points: CardResp[] // 顧客擁有的點卡與票券
  course: CardResp[] // 顧客擁有的課卡
  contractCategories: Record<string, ContractCategoryResp[]> // 點卡擁有的活動類別
}

const state: MerchantState = {
  profile: new AccountResp(),
  points: [],
  course: [],
  tokens: [],
  contractCategories: {}
};

export default {
  state,
  getters: {
    getPoints: (state: MerchantState): PointCardDTO[] => {
      const issueList: PointCardDTO[] = _.filter(
        state.tokens,
        (token) => {
          const issueCommon = _.filter(
            token.issueContracts,
            (issue) => issue.isMemberTrigger === false
          );
          return issueCommon.length > 0;
        }
      ).map((token) => {
        const userToken = _.find(state.points, { address: token.address });
        const userAmount: number = userToken?.amount ?? 0;
        const card = new PointCardDTO({
          id: token.address as string,
          name: token.name,
          image: token.image,
          amount: userAmount,
          contracts: [] as Contract[],
          type: EventType.Issue,
          nearestExpiryDate: userToken?.nearestExpiryDate,
          nearestExpiryAmount: userToken?.nearestExpiryAmount
        });

        card.contracts = _.filter(
          token.issueContracts,
          (issue) => issue.isMemberTrigger === false
        ).map((issue) => {
          const contract = new Contract();
          contract.mapFromApiModel(issue);
          return contract;
        });
        return card;
      });
      return issueList;
    },
    getOnlineIssue: (state: MerchantState): PointCardDTO[] => {
      const onlineIssue: PointCardDTO[] = _.filter(
        state.tokens,
        (token) => {
          const issueCommon = _.filter(
            token.issueContracts,
            (issue) => issue.isMemberTrigger === true
          );
          return issueCommon.length > 0;
        }
      ).map((token) => {
        const userToken = _.find(state.points, { address: token.address });

        const userAmount: number = userToken?.amount ?? 0;
        const result = new PointCardDTO({
          id: token.address as string,
          name: token.name,
          image: token.image,
          amount: userAmount,
          contracts: [] as Contract[],
          type: EventType.Issue,
          nearestExpiryDate: userToken?.nearestExpiryDate,
          nearestExpiryAmount: userToken?.nearestExpiryAmount
        });

        result.contracts = _.filter(
          token.issueContracts,
          (issue) => issue.isMemberTrigger === true
        ).map((issue) => {
          const result = new Contract();
          result.mapFromApiModel(issue);
          return result;
        });
        return result;
      });
      return onlineIssue;
    },
    getRedeems: (state: MerchantState): PointCardDTO[] => {
      const redeemList: PointCardDTO[] = state.tokens
        .map(token => {
          const relevantRedeemContracts = _.filter(
            token.redeemContracts,
            (redeem) =>
              redeem.isMemberTrigger === false && redeem.isVoucher === false
          );
          return { token, relevantRedeemContracts };
        })
        .filter(({ relevantRedeemContracts }) => relevantRedeemContracts.length > 0)
        .map(({ token, relevantRedeemContracts }) => {
          const userToken = _.find(state.points, { address: token.address });
          const userAmount: number = userToken?.amount ?? 0;

          const result = new PointCardDTO({
            id: token.address as string,
            name: token.name,
            image: token.image,
            amount: userAmount,
            contracts: relevantRedeemContracts.map((redeem) => {
              const contract = new Contract();
              contract.mapFromApiModel(redeem);
              return contract;
            }),
            type: EventType.Redeem,
            nearestExpiryDate: userToken?.nearestExpiryDate,
            nearestExpiryAmount: userToken?.nearestExpiryAmount
          });

          return result;
        });
      return redeemList;
    },
    getOnlineRedeems: (state: MerchantState): PointCardDTO[] => {
      const redeemList: PointCardDTO[] = state.tokens
        .map(token => {
          const relevantRedeemContracts = _.filter(
            token.redeemContracts,
            (redeem) =>
              redeem.isMemberTrigger === true && redeem.isVoucher === false
          );
          return { token, relevantRedeemContracts };
        })
        .filter(({ relevantRedeemContracts }) => relevantRedeemContracts.length > 0)
        .map(({ token, relevantRedeemContracts }) => {
          const userToken = _.find(state.points, { address: token.address });
          const userAmount: number = userToken?.amount ?? 0;

          const result = new PointCardDTO({
            id: token.address as string,
            name: token.name,
            image: token.image,
            amount: userAmount,
            contracts: relevantRedeemContracts.map((redeem) => {
              const contract = new Contract();
              contract.mapFromApiModel(redeem);
              return contract;
            }),
            type: EventType.Redeem,
            nearestExpiryDate: userToken?.nearestExpiryDate,
            nearestExpiryAmount: userToken?.nearestExpiryAmount
          });

          return result;
        });
      return redeemList;
    },
    getCourse: (state: MerchantState): CourseCardPropDTO[] => {
      const courseList = _.filter(
        state.course,
        (token) => token.merchantAddress === state.profile.walletAddress
      );
      const result: CourseCardPropDTO[] = [];
      for (const item of courseList) {
        const parse = new CourseCardPropDTO();
        parse.name = item.name;
        parse.address = item.address;
        parse.image = item.image;
        parse.timeType = item.courseTimeType;
        parse.timeDescription = item.courseTimeDescription ?? '';
        parse.date = `${getDateWithoutTime(
          item.startOn
        )} ~ ${getDateWithoutTime(item.endOn)}`;
        parse.remain = item.amount ?? 0;
        parse.instructor = item.instructor;
        parse.courseTimes = item.courseTimes;
        result.push(parse);
      }
      return result;
    },
    getETickets(state: MerchantState): PointCardDTO[] {
      const ticketList: PointCardDTO[] = _.filter(
        state.points,
        (point) => {
          if (!_.find(state.tokens, { address: point.address })) {
            return false;
          }

          const ticketCommon = _.filter(
            point.redeemContracts,
            (redeem) => redeem.isVoucher
          );
          return ticketCommon.length > 0;
        }
      ).map((point) => {
        const result = new PointCardDTO({
          id: point.address as string,
          name: point.name,
          image: point.image,
          amount: point.amount ?? 0,
          contracts: [] as Contract[],
          type: EventType.Redeem,
          nearestExpiryDate: point.nearestExpiryDate,
          nearestExpiryAmount: point.nearestExpiryAmount
        });

        result.contracts = _.filter(
          point.redeemContracts,
          (redeem) => redeem.isVoucher
        ).map((redeem) => {
          const result = new Contract();
          result.mapFromApiModel(redeem);
          return result;
        });
        return result;
      });
      return ticketList;
    },
    getCardContractCategories: (state: MerchantState): Record<string, ContractCategoryResp[]> | undefined => {
      return state.contractCategories;
    },
    getProfile: (state: MerchantState): AccountResp => {
      return state.profile;
    }
  },
  mutations: {
    setProfile(state: MerchantState, profile: AccountResp) {
      state.profile = profile;
    },
    setPoint(state: MerchantState, points: CardResp[]) {
      state.points = points;
    },
    setCourse(state: MerchantState, courses: CardResp[]) {
      state.course = courses;
    },
    setCards(state: MerchantState, merchantCards: MerchantCardsResp) {
      state.tokens = merchantCards.points;
    },
    setCardContractCategories(state: MerchantState, contractCategories: Record<string, ContractCategoryResp[]>) {
      state.contractCategories = contractCategories;
    }
  },
  namespaced: true
};
